<nb-card style="min-width:700px; max-width:900px">
  <nb-card-header class="d-flex justify-content-between align-items-center">
    <h5 class="mb-0">{{ product ? 'Edit Product' : 'Add New Product' }}</h5>
    <button class="btn btn-ghost-danger btn-sm" (click)="close()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>

  <nb-card-body class="dialog-body">
    <form [formGroup]="productForm" (ngSubmit)="onSubmitProduct()">
      <!-- Global Product Linking Panel — shown in both create and edit mode -->
      <div class="mb-3">
        <div class="card border-0 shadow-sm">
          <div class="card-header d-flex align-items-center justify-content-between py-2 px-3"
               style="cursor: pointer; background: #f8f9fa;"
               (click)="globalPanelOpen = !globalPanelOpen">
            <span class="d-flex align-items-center gap-2">
              <i class="fas fa-link"></i>
              <strong>Global Product Catalog</strong>
            </span>
            <div class="d-flex align-items-center gap-2">
              <span class="badge"
                [ngClass]="{
                  'bg-success': (product?.globalProductId || selectedGlobalProduct) && syncStatus === 'synced',
                  'bg-warning text-dark': (product?.globalProductId || selectedGlobalProduct) && syncStatus !== 'synced',
                  'bg-secondary': !product?.globalProductId && !selectedGlobalProduct && !pendingGlobalProductLink,
                  'bg-info text-dark': !product && pendingGlobalProductLink
                }">
                <ng-container *ngIf="!product && pendingGlobalProductLink">Will link on save</ng-container>
                <ng-container *ngIf="product?.globalProductId || selectedGlobalProduct">{{ syncStatus === 'synced' ? 'Synced' : 'Overrides active' }}</ng-container>
                <ng-container *ngIf="!product?.globalProductId && !selectedGlobalProduct && !pendingGlobalProductLink">Not linked</ng-container>
              </span>
              <i [class]="globalPanelOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </div>
          </div>
          <div [class.d-none]="!globalPanelOpen" class="card-body">

            <!-- CREATE MODE: pending link selected -->
            <div *ngIf="!product && pendingGlobalProductLink">
              <div class="alert alert-success d-flex align-items-center justify-content-between mb-0">
                <span>
                  <i class="fas fa-link"></i>
                  Will link to: <strong>{{ pendingGlobalProductLink.name }}</strong>
                  <span *ngIf="pendingGlobalProductLink.barcode" class="text-muted ms-1 small">
                    (EAN: {{ pendingGlobalProductLink.barcode }})
                  </span>
                </span>
                <button class="btn btn-sm btn-outline-danger" type="button" (click)="clearPendingLink()">
                  <i class="fas fa-times"></i> Remove
                </button>
              </div>
              <p class="text-muted small mt-2 mb-0">Form fields have been pre-filled from the global product. The link will be saved automatically when you create the product.</p>
            </div>

            <!-- CREATE MODE: no pending link — show search -->
            <div *ngIf="!product && !pendingGlobalProductLink">
              <p class="text-muted small mb-2">Optionally link this new product to a global product template. Fields will be pre-filled automatically.</p>
              <div class="global-search-container">
                <div class="input-group mb-2">
                  <input type="text" class="form-control" placeholder="Search by name or barcode…"
                    [(ngModel)]="globalSearchTerm" [ngModelOptions]="{standalone: true}"
                    (ngModelChange)="onGlobalSearchInput($event)">
                  <span class="input-group-text">
                    <span *ngIf="!globalSearchLoading"><i class="fas fa-search"></i></span>
                    <span *ngIf="globalSearchLoading" class="spinner-border spinner-border-sm" role="status"></span>
                  </span>
                </div>
                <div class="global-search-results" *ngIf="globalSearchResults.length > 0">
                  <div class="global-search-result-item"
                    *ngFor="let gp of globalSearchResults"
                    (click)="selectGlobalProductResult(gp)">
                    <strong>{{ gp.name }}</strong>
                    <span *ngIf="gp.barcode" class="text-muted ms-2 small">EAN: {{ gp.barcode }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- EDIT MODE: not linked — show search -->
            <div *ngIf="product && !product?.globalProductId && !selectedGlobalProduct">
              <p class="text-muted small mb-2">Link this product to a global product template to enable cross-store sync.</p>
              <div class="global-search-container">
                <div class="input-group mb-2">
                  <input type="text" class="form-control" placeholder="Search by name or barcode…"
                    [(ngModel)]="globalSearchTerm" [ngModelOptions]="{standalone: true}"
                    (ngModelChange)="onGlobalSearchInput($event)">
                  <span class="input-group-text">
                    <span *ngIf="!globalSearchLoading"><i class="fas fa-search"></i></span>
                    <span *ngIf="globalSearchLoading" class="spinner-border spinner-border-sm" role="status"></span>
                  </span>
                </div>
                <div class="global-search-results" *ngIf="globalSearchResults.length > 0">
                  <div class="global-search-result-item"
                    *ngFor="let gp of globalSearchResults"
                    (click)="selectGlobalProductResult(gp)">
                    <strong>{{ gp.name }}</strong>
                    <span *ngIf="gp.barcode" class="text-muted ms-2 small">EAN: {{ gp.barcode }}</span>
                  </div>
                </div>
              </div>
              <div *ngIf="globalLinkLoading" class="text-center py-2">
                <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                <small class="text-muted ms-2">Linking…</small>
              </div>
            </div>

            <!-- EDIT MODE: linked — show info + field locks -->
            <div *ngIf="product && (product?.globalProductId || selectedGlobalProduct)">
              <div class="linked-product-info mb-3 d-flex align-items-center gap-2">
                <i class="fas fa-check-circle text-success"></i>
                <span>Linked to: <strong>{{ selectedGlobalProduct?.name || product?.globalProductId }}</strong></span>
                <button class="btn btn-sm btn-outline-danger ms-2" type="button"
                  (click)="unlinkGlobalProduct()" [disabled]="globalLinkLoading">
                  <i class="fas fa-link"></i> Unlink
                </button>
              </div>

              <div class="field-locks-section">
                <h6 class="section-title">Field Locks</h6>
                <p class="text-muted small mb-2">Lock fields to override the global value for this store product. Unlocked fields will inherit from the global product when synced.</p>
                <div class="field-locks-grid">
                  <div class="field-lock-row" *ngFor="let field of lockableFields">
                    <span class="field-label">{{ field.label }}</span>
                    <button class="btn btn-sm"
                      [ngClass]="fieldLocks[field.key] ? 'btn-outline-warning' : 'btn-outline-secondary'"
                      type="button"
                      (click)="toggleFieldLock(field.key)"
                      [title]="fieldLocks[field.key] ? 'Override active (click to inherit)' : 'Inheriting from global (click to override)'">
                      <i [class]="fieldLocks[field.key] ? 'fas fa-lock text-warning' : 'fas fa-lock-open text-muted'"></i>
                    </button>
                  </div>
                </div>
                <div class="mt-3 d-flex align-items-center gap-3">
                  <button class="btn btn-primary btn-sm" type="button"
                    (click)="saveFieldLocks()" [disabled]="fieldLocksSaving">
                    <span *ngIf="fieldLocksSaving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    <i *ngIf="!fieldLocksSaving" class="fas fa-save"></i>
                    {{ fieldLocksSaving ? 'Saving…' : 'Save Field Locks' }}
                  </button>
                  <span class="text-muted small">{{ lockedFieldsCount }} field(s) overriding global values</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div class="row">
        <!-- Basic Information -->
        <div class="col-md-6">
          <h6 class="section-title">Basic Information</h6>

          <div class="mb-3">
            <label class="form-label">Product Name *</label>
            <input type="text" class="form-control" formControlName="name" placeholder="Enter product name">
            <div *ngIf="productForm.get('name')?.invalid && productForm.get('name')?.touched" class="text-danger small mt-1">
              Product name is required (min 2 characters)
            </div>
          </div>

          <div *ngIf="skuRequirementEnabled" class="mb-3">
            <label class="form-label">SKU *</label>
            <input type="text" class="form-control" formControlName="sku" placeholder="Enter unique SKU">
            <div *ngIf="productForm.get('sku')?.invalid && productForm.get('sku')?.touched" class="text-danger small mt-1">
              SKU is required
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Barcode</label>
            <input type="text" class="form-control" formControlName="barcode" placeholder="Enter barcode" (input)="onBarcodeInput()" (blur)="onBarcodeBlur()">
            <small class="form-text text-muted">
              Optional barcode for scanning (EAN-13 or short internal code)
              <span *ngIf="detectingEAN" class="ms-2 text-primary">
                <nb-icon icon="loader-outline"></nb-icon> detecting deposit type...
              </span>
            </small>
            <div *ngIf="eanAutoDetectHint" class="alert alert-info py-1 px-2 mt-1 small">
              <nb-icon icon="flash-outline" class="me-1"></nb-icon>
              {{ eanAutoDetectHint }}
            </div>
            <div *ngIf="productForm.get('barcode')?.invalid && productForm.get('barcode')?.touched" class="text-danger small mt-1">
              <span *ngIf="productForm.get('barcode')?.errors?.['invalidBarcode']">
                {{ productForm.get('barcode')?.errors?.['invalidBarcode']?.message }}
              </span>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">PLU <span class="text-muted small">(7-digit, optional)</span></label>
            <input type="text" class="form-control" formControlName="plu" maxlength="7" placeholder="7-digit PLU (e.g. 2080990)">
            <small class="form-text text-muted">7-digit PLU code for scale-generated EAN-13 barcodes (weighted products)</small>
            <div *ngIf="productForm.get('soldByWeight')?.value" class="text-muted small mt-1">
              First 2 digits are the prefix: 20 = price-encoded, 21 = weight-encoded
            </div>
            <div *ngIf="productForm.get('plu')?.errors?.['maxlength']" class="text-danger small mt-1">
              PLU must be 7 digits or fewer
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Description</label>
            <textarea class="form-control" formControlName="description" rows="3" placeholder="Product description"></textarea>
          </div>

          <div class="mb-3">
            <label class="form-label">Category *</label>
            <select class="form-select" formControlName="categoryId">
              <option value="">Select Category</option>
              <option *ngFor="let category of categories" [value]="category._id">{{ category.name }}</option>
            </select>
            <div *ngIf="productForm.get('categoryId')?.invalid && productForm.get('categoryId')?.touched" class="text-danger small mt-1">
              Category is required
            </div>
            <small class="text-muted" *ngIf="catalogImportCategoryHint">{{ catalogImportCategoryHint }}</small>
          </div>

          <h6 class="section-title">Classification</h6>

          <div class="mb-3">
            <label class="form-label">Search DSK classification</label>
            <ng-select
              [items]="dskSearchResults"
              bindLabel="mainGroup"
              placeholder="Type to search DSK groups…"
              [typeahead]="dskSearchInput$"
              [loading]="dskSearchLoading"
              [(ngModel)]="selectedDskSearch"
              [ngModelOptions]="{standalone: true}"
              (change)="onDskSearchSelected($event)"
              [clearable]="true"
              [minTermLength]="1">
              <ng-template ng-option-tmp let-item="item">
                <div>{{ formatDskSearchOptionLabel(item) }}</div>
                <small class="text-muted" *ngIf="formatDskSearchOptionMeta(item)">{{ formatDskSearchOptionMeta(item) }}</small>
              </ng-template>
            </ng-select>
            <small class="text-muted">Select a row to fill all classification fields, or edit them manually below.</small>
          </div>

          <div class="row">
            <div class="col-md-6 mb-3">
              <label class="form-label">Main Group</label>
              <input type="text" class="form-control" formControlName="mainGroup" placeholder="DSK main group code">
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Product Group</label>
              <input type="text" class="form-control" formControlName="productGroup" placeholder="DSK product group code">
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Sub Group</label>
              <input type="text" class="form-control" formControlName="subGroup" placeholder="DSK sub-group code">
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">DSK Assortment ID</label>
              <input type="text" class="form-control" formControlName="dskAssortmentID" placeholder="DSK assortment ID">
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Family</label>
              <input type="text" class="form-control" formControlName="family" placeholder="Internal family mapping">
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Discount Group</label>
              <input type="text" class="form-control" formControlName="discountGroup" placeholder="Campaign/discount group">
            </div>
          </div>
        </div>

        <!-- Pricing -->
        <div class="col-md-6">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="section-title mb-0">Pricing & Tax</h6>
            <button *ngIf="product?._id" type="button" class="btn btn-sm btn-outline-secondary"
              (click)="openPriceHistoryDialog()">
              <nb-icon icon="clock-outline" class="me-1"></nb-icon>
              View Price History
            </button>
          </div>

          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label">Price *</label>
                <div class="input-group">
                  <input type="number" class="form-control" formControlName="price" step="0.01" min="0">
                  <span class="input-group-text">{{ storeCurrency || 'DKK' }}</span>
                </div>
                <small class="form-text text-muted">Currency inherited from store settings</small>
                <div *ngIf="productForm.get('price')?.invalid && productForm.get('price')?.touched" class="text-danger small mt-1">
                  Price is required and must be greater than 0
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label">Original Price</label>
                <div class="input-group">
                  <input type="number" class="form-control" formControlName="originalPrice" step="0.01" min="0">
                  <span class="input-group-text">{{ storeCurrency || 'DKK' }}</span>
                </div>
                <small class="form-text text-muted">For showing discounts</small>
              </div>
            </div>
          </div>

          <!-- Scheduled price change (edit mode only) — full width below Price / Original Price -->
          <div class="row" *ngIf="product?._id">
            <div class="col-12">
              <div class="scheduled-price-panel mt-2 mb-3">
                <button type="button" class="btn btn-sm btn-outline-primary w-100 scheduled-toggle-btn"
                  (click)="toggleSchedulePanel()">
                  <nb-icon [icon]="schedulePanelOpen ? 'chevron-up-outline' : 'chevron-down-outline'" class="me-1"></nb-icon>
                  Schedule a future price change
                </button>

                <div *ngIf="schedulePanelOpen" class="scheduled-price-form mt-2">
                  <div class="row g-2">
                    <div class="col-12">
                      <label class="form-label">New price</label>
                      <div class="input-group price-input-group">
                        <input type="number" class="form-control" [(ngModel)]="schedulePrice" [ngModelOptions]="{standalone: true}"
                          step="0.01" min="0.01" inputmode="decimal" placeholder="0.00"
                          [class.is-invalid]="schedulePriceError"
                          (blur)="schedulePriceTouched = true">
                        <span class="input-group-text">{{ storeCurrency || 'DKK' }}</span>
                      </div>
                      <div class="pricing-validation-slot">
                        <div class="text-danger small" *ngIf="schedulePriceError">{{ schedulePriceError }}</div>
                      </div>
                    </div>
                    <div class="col-12 col-md-6">
                      <label class="form-label">Activates at</label>
                      <input type="datetime-local" class="form-control" [(ngModel)]="scheduleActivatesAt"
                        [ngModelOptions]="{standalone: true}"
                        [class.is-invalid]="scheduleDateError"
                        (blur)="scheduleDateTouched = true">
                      <div class="pricing-validation-slot">
                        <div class="text-danger small" *ngIf="scheduleDateError">{{ scheduleDateError }}</div>
                      </div>
                    </div>
                    <div class="col-12 col-md-6">
                      <label class="form-label">Ends at <span class="text-muted small">(optional)</span></label>
                      <input type="datetime-local" class="form-control" [(ngModel)]="scheduleDeactivatesAt"
                        [ngModelOptions]="{standalone: true}"
                        [class.is-invalid]="scheduleDeactivatesAtError"
                        (blur)="scheduleDeactivatesTouched = true">
                      <div class="pricing-validation-slot">
                        <div class="text-danger small" *ngIf="scheduleDeactivatesAtError">{{ scheduleDeactivatesAtError }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="pricing-validation-slot">
                    <div class="text-danger small" *ngIf="scheduleInlineError">{{ scheduleInlineError }}</div>
                  </div>
                  <button type="button" class="btn btn-primary btn-sm mt-1"
                    [disabled]="!canSubmitSchedule() || scheduleSaving"
                    (click)="scheduleFuturePrice()">
                    <span *ngIf="scheduleSaving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    Schedule
                  </button>
                </div>

                <div *ngIf="upcomingPriceSchedule && !editingUpcomingSchedule" class="upcoming-price-card mt-3">
                  <div class="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div class="fw-semibold">Upcoming price change</div>
                      <div class="small text-muted mt-1">
                        {{ formatCurrency(upcomingPriceSchedule.price) }} on {{ formatScheduleDate(upcomingPriceSchedule.activatesAt) }}
                        <span *ngIf="upcomingPriceSchedule.deactivatesAt"> · ends {{ formatScheduleDate(upcomingPriceSchedule.deactivatesAt) }}</span>
                      </div>
                    </div>
                    <span class="badge bg-info text-dark">Scheduled</span>
                  </div>
                  <div class="d-flex flex-wrap gap-2 mt-2">
                    <button type="button" class="btn btn-sm btn-outline-primary" [disabled]="scheduleSaving"
                      (click)="startEditUpcomingSchedule()">Edit</button>
                    <button type="button" class="btn btn-sm btn-outline-danger" [disabled]="scheduleSaving"
                      (click)="cancelUpcomingSchedule()">Cancel</button>
                  </div>
                </div>

                <div *ngIf="activePriceSchedule && !editingActiveScheduleEnd" class="active-price-card mt-3">
                  <div class="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div class="fw-semibold">Active scheduled price</div>
                      <div class="small text-muted mt-1">
                        {{ formatCurrency(activePriceSchedule.price) }} since {{ formatScheduleDate(activePriceSchedule.activatesAt) }}
                        <span *ngIf="activePriceSchedule.deactivatesAt"> · ends {{ formatScheduleDate(activePriceSchedule.deactivatesAt) }}</span>
                      </div>
                    </div>
                    <span class="badge bg-success">Active</span>
                  </div>
                  <button type="button" class="btn btn-sm btn-outline-primary mt-2" [disabled]="scheduleSaving"
                    (click)="startEditActiveScheduleEnd()">Set end</button>
                </div>

                <div *ngIf="activePriceSchedule && editingActiveScheduleEnd" class="active-price-edit mt-3">
                  <div class="fw-semibold mb-2">Set end for active price</div>
                  <label class="form-label">Ends at <span class="text-muted small">(optional — leave empty to clear)</span></label>
                  <input type="datetime-local" class="form-control" [(ngModel)]="activeScheduleDeactivatesAt"
                    [ngModelOptions]="{standalone: true}"
                    [class.is-invalid]="activeScheduleDeactivatesAtError"
                    (blur)="activeScheduleDeactivatesTouched = true">
                  <div class="pricing-validation-slot">
                    <div class="text-danger small" *ngIf="activeScheduleDeactivatesAtError">{{ activeScheduleDeactivatesAtError }}</div>
                  </div>
                  <div class="d-flex flex-wrap gap-2 mt-2">
                    <button type="button" class="btn btn-sm btn-primary"
                      [disabled]="!canSubmitActiveScheduleEnd() || scheduleSaving"
                      (click)="saveActiveScheduleEnd()">Save end</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                      [disabled]="scheduleSaving"
                      (click)="cancelEditActiveScheduleEnd()">Cancel</button>
                    <button type="button" class="btn btn-sm btn-outline-danger"
                      *ngIf="activePriceSchedule.deactivatesAt"
                      [disabled]="scheduleSaving"
                      (click)="clearActiveScheduleEnd()">Clear end</button>
                  </div>
                </div>

                <div *ngIf="upcomingPriceSchedule && editingUpcomingSchedule" class="upcoming-price-edit mt-3">
                  <div class="fw-semibold mb-2">Edit upcoming price change</div>
                  <div class="row g-2">
                    <div class="col-12" *ngIf="upcomingPriceSchedule.status !== 'active'">
                      <label class="form-label">New price</label>
                      <div class="input-group price-input-group">
                        <input type="number" class="form-control" [(ngModel)]="editSchedulePrice" [ngModelOptions]="{standalone: true}"
                          step="0.01" min="0.01" inputmode="decimal"
                          [class.is-invalid]="editSchedulePriceError"
                          (blur)="editSchedulePriceTouched = true">
                        <span class="input-group-text">{{ storeCurrency || 'DKK' }}</span>
                      </div>
                      <div class="pricing-validation-slot">
                        <div class="text-danger small" *ngIf="editSchedulePriceError">{{ editSchedulePriceError }}</div>
                      </div>
                    </div>
                    <div class="col-12 col-md-6" *ngIf="upcomingPriceSchedule.status !== 'active'">
                      <label class="form-label">Activates at</label>
                      <input type="datetime-local" class="form-control" [(ngModel)]="editScheduleActivatesAt"
                        [ngModelOptions]="{standalone: true}"
                        [class.is-invalid]="editScheduleDateError"
                        (blur)="editScheduleDateTouched = true">
                      <div class="pricing-validation-slot">
                        <div class="text-danger small" *ngIf="editScheduleDateError">{{ editScheduleDateError }}</div>
                      </div>
                    </div>
                    <div class="col-12 col-md-6">
                      <label class="form-label">Ends at <span class="text-muted small">(optional)</span></label>
                      <input type="datetime-local" class="form-control" [(ngModel)]="editScheduleDeactivatesAt"
                        [ngModelOptions]="{standalone: true}"
                        [class.is-invalid]="editScheduleDeactivatesAtError"
                        (blur)="editScheduleDeactivatesTouched = true">
                      <div class="pricing-validation-slot">
                        <div class="text-danger small" *ngIf="editScheduleDeactivatesAtError">{{ editScheduleDeactivatesAtError }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="pricing-validation-slot">
                    <div class="text-danger small" *ngIf="scheduleInlineError">{{ scheduleInlineError }}</div>
                  </div>
                  <div class="d-flex flex-wrap gap-2 mt-1">
                    <button type="button" class="btn btn-sm btn-primary"
                      [disabled]="!canSubmitEditSchedule() || scheduleSaving"
                      (click)="saveUpcomingScheduleEdit()">Save</button>
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                      [disabled]="scheduleSaving"
                      (click)="cancelEditUpcomingSchedule()">Discard</button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="mb-3">
                <label class="form-label">Cost Price ({{ storeCurrency || 'DKK' }}) <span class="text-muted small">(Optional)</span></label>
                <input type="number" class="form-control" formControlName="costPrice"
                       placeholder="Purchase / indkøbspris" min="0" step="0.01">
                <small class="form-text text-muted">Internal cost / purchase price — not shown to customers</small>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <div class="mb-3">
                <label class="form-label">Tax Rate (%)</label>
                <input type="number" class="form-control" formControlName="taxRate" min="0" max="100">
              </div>
            </div>
          </div>

          <div class="mb-3">
            <div class="form-check">
              <input type="checkbox" class="form-check-input" formControlName="taxIncluded" id="taxIncluded">
              <label class="form-check-label" for="taxIncluded">Tax Included in Price</label>
            </div>
          </div>

          <h6 class="section-title mt-3 mb-2">Unit Pricing</h6>
          <div class="row unit-pricing-row">
            <div class="col-md-8">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-2">
                    <label class="form-label">Unit Code (Enhedskode)</label>
                    <select class="form-select" formControlName="unitCode">
                      <option value="">— Select —</option>
                      <option *ngFor="let code of unitCodeOptions" [value]="code">{{ code }}</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-2">
                    <label class="form-label">Unit Content (Indhold pr. enhed)</label>
                    <input type="number" class="form-control" formControlName="unitContent" min="0" step="any" placeholder="0"
                      [class.is-invalid]="productForm.get('unitContent')?.invalid && productForm.get('unitContent')?.touched">
                  </div>
                </div>
              </div>
              <div class="unit-pricing-validation-slot">
                <div class="text-danger small"
                  *ngIf="productForm.get('unitContent')?.errors?.['unitContentZero'] && productForm.get('unitContent')?.touched">
                  {{ productForm.get('unitContent')?.errors?.['unitContentZero']?.message }}
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="mb-2">
                <label class="form-label">Literpris</label>
                <div class="liter-price-display-slot">
                  <span class="liter-price-value">{{ displayedLiterPrice }}</span>
                </div>
              </div>
            </div>
          </div>

          <div *ngIf="product?._id && productDiscountGroupLabel" class="alert alert-info py-2 px-3 small mb-2">
            <div>
              This product belongs to discount group
              <strong>{{ productDiscountGroupLabel }}</strong>.
            </div>
            <div *ngIf="discountGroupOffersLoading" class="text-muted mt-1">Checking group offers…</div>
            <div *ngIf="!discountGroupOffersLoading && primaryActiveDiscountGroupOffer" class="mt-1">
              Group offer {{ primaryActiveDiscountGroupOffer.requiredQuantity }} for
              {{ primaryActiveDiscountGroupOffer.offerPrice }} DKK applies and overrides this product's own offers.
            </div>
          </div>

          <div *ngIf="product?._id" class="multi-buy-offers-section mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="section-title mb-0">Multi-Buy Offers</h6>
              <button type="button" class="btn btn-sm btn-outline-primary" (click)="openAddOfferForm()"
                [disabled]="showOfferForm && !editingOfferId">
                <nb-icon icon="plus-outline" class="me-1"></nb-icon>
                Add Offer
              </button>
            </div>
            <p class="text-muted small mb-2">Total price for the required quantity (e.g. "3 for 50 {{ storeCurrency || 'DKK' }}"). Saved separately from the main product form.</p>

            <div *ngIf="offersLoading" class="text-muted small py-2">Loading offers…</div>

            <div *ngIf="!offersLoading && multiBuyOffers.length === 0 && !showOfferForm" class="text-muted small py-2">
              No multi-buy offers configured yet.
            </div>

            <div *ngIf="!offersLoading && multiBuyOffers.length > 0" class="table-responsive multi-buy-offers-table">
              <table class="table table-sm align-middle mb-2">
                <thead>
                  <tr>
                    <th>Qty</th>
                    <th>Offer price</th>
                    <th>Valid from</th>
                    <th>Valid to</th>
                    <th>Status</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let offer of multiBuyOffers">
                    <td>{{ offer.requiredQuantity }}</td>
                    <td>{{ formatCurrency(offer.offerPrice) }}</td>
                    <td>{{ formatOfferDate(offer.validFrom) }}</td>
                    <td>{{ formatOfferDate(offer.validTo) }}</td>
                    <td>
                      <span class="badge" [ngClass]="getOfferStatus(offer).badgeClass">
                        {{ getOfferStatus(offer).label }}
                      </span>
                    </td>
                    <td class="text-end">
                      <div class="d-flex flex-wrap justify-content-end gap-1">
                        <button type="button" class="btn btn-sm btn-outline-secondary"
                          (click)="toggleOfferActive(offer)">
                          {{ offer.isActive === false ? 'Enable' : 'Disable' }}
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-primary"
                          (click)="startEditOffer(offer)">Edit</button>
                        <button type="button" class="btn btn-sm btn-outline-danger"
                          (click)="deleteOffer(offer)">Delete</button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div *ngIf="showOfferForm" class="multi-buy-offer-form">
              <div class="fw-semibold mb-2">{{ editingOfferId ? 'Edit offer' : 'Add offer' }}</div>
              <div class="row g-2">
                <div class="col-12 col-md-6">
                  <label class="form-label">Required quantity</label>
                  <input type="number" class="form-control" [(ngModel)]="offerRequiredQuantity"
                    [ngModelOptions]="{standalone: true}" min="2" step="1" inputmode="numeric" placeholder="3"
                    [class.is-invalid]="offerQtyError"
                    (blur)="offerQtyTouched = true">
                  <div class="pricing-validation-slot">
                    <div class="text-danger small" *ngIf="offerQtyError">{{ offerQtyError }}</div>
                  </div>
                </div>
                <div class="col-12 col-md-6">
                  <label class="form-label">Offer price (total)</label>
                  <div class="input-group price-input-group">
                    <input type="number" class="form-control" [(ngModel)]="offerPrice"
                      [ngModelOptions]="{standalone: true}" min="0" step="0.01" inputmode="decimal" placeholder="50.00"
                      [class.is-invalid]="offerPriceError"
                      (blur)="offerPriceTouched = true">
                    <span class="input-group-text">{{ storeCurrency || 'DKK' }}</span>
                  </div>
                  <div class="pricing-validation-slot">
                    <div class="text-danger small" *ngIf="offerPriceError">{{ offerPriceError }}</div>
                  </div>
                </div>
                <div class="col-12 col-md-6">
                  <label class="form-label">Valid from <span class="text-muted small">(optional)</span></label>
                  <input type="datetime-local" class="form-control" [(ngModel)]="offerValidFrom"
                    [ngModelOptions]="{standalone: true}">
                </div>
                <div class="col-12 col-md-6">
                  <label class="form-label">Valid to <span class="text-muted small">(optional)</span></label>
                  <input type="datetime-local" class="form-control" [(ngModel)]="offerValidTo"
                    [ngModelOptions]="{standalone: true}">
                </div>
              </div>
              <div class="pricing-validation-slot">
                <div class="text-danger small" *ngIf="offerInlineError">{{ offerInlineError }}</div>
              </div>
              <div class="d-flex flex-wrap gap-2 mt-1">
                <button type="button" class="btn btn-sm btn-primary"
                  [disabled]="!canSubmitOffer() || offerSaving"
                  (click)="saveOffer()">
                  <span *ngIf="offerSaving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  {{ editingOfferId ? 'Update offer' : 'Save offer' }}
                </button>
                <button type="button" class="btn btn-sm btn-outline-secondary"
                  [disabled]="offerSaving"
                  (click)="cancelOfferForm()">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- INGREDIENTS & ALLERGENS + NUTRITION -->
      <div class="row" *ngIf="showAllergens || showNutrition">
        <div class="col-12">
          <ng-container *ngIf="showAllergens">
          <h6 class="section-title">Ingredients &amp; Allergens</h6>
          <div class="row mb-3">
            <div class="col-12 mb-3">
              <label class="form-label">Ingredients</label>
              <textarea class="form-control ingredients-textarea" formControlName="ingredients" rows="5"
                placeholder="Paste full ingredient list from supplier packaging"></textarea>
              <div class="ingredients-meta-row">
                <small class="text-muted">{{ ingredientsCharCount }} / {{ ingredientsMaxLength }} characters</small>
                <small class="text-warning ingredients-warn"
                  *ngIf="ingredientsCharCount >= ingredientsSoftWarnLength && ingredientsCharCount <= ingredientsMaxLength">
                  Approaching maximum length — backend will reject over {{ ingredientsMaxLength }} characters
                </small>
                <small class="text-danger ingredients-warn"
                  *ngIf="ingredientsCharCount > ingredientsMaxLength">
                  Exceeds maximum length of {{ ingredientsMaxLength }} characters
                </small>
              </div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">Allergens</label>
              <div class="allergen-checkbox-grid">
                <div class="form-check" *ngFor="let allergen of allergenOptions">
                  <input class="form-check-input" type="checkbox"
                    [id]="'store-allergen-' + allergen.value"
                    [checked]="isAllergenSelected('allergens', allergen.value)"
                    (change)="toggleAllergen('allergens', allergen.value, $any($event.target).checked)">
                  <label class="form-check-label" [for]="'store-allergen-' + allergen.value">{{ allergen.label }}</label>
                </div>
              </div>
            </div>
            <div class="col-md-6 mb-3">
              <label class="form-label">May contain traces of</label>
              <div class="allergen-checkbox-grid">
                <div class="form-check" *ngFor="let allergen of allergenOptions">
                  <input class="form-check-input" type="checkbox"
                    [id]="'store-trace-' + allergen.value"
                    [checked]="isAllergenSelected('allergenTraces', allergen.value)"
                    (change)="toggleAllergen('allergenTraces', allergen.value, $any($event.target).checked)">
                  <label class="form-check-label" [for]="'store-trace-' + allergen.value">{{ allergen.label }}</label>
                </div>
              </div>
            </div>
          </div>
          </ng-container>

          <ng-container *ngIf="showNutrition">
          <h6 class="section-title">Nutrition Facts</h6>
          <div class="row mb-3 nutrition-grid" formGroupName="nutrition">
            <div class="col-md-4 mb-2" *ngFor="let field of nutritionFields">
              <label class="form-label">{{ field.label }}</label>
              <div class="input-group">
                <input type="number" class="form-control" [formControlName]="field.key"
                  min="0" step="any" inputmode="decimal" placeholder="0"
                  [class.is-invalid]="productForm.get('nutrition.' + field.key)?.invalid && productForm.get('nutrition.' + field.key)?.touched">
                <span class="input-group-text">{{ field.unit }}</span>
              </div>
              <div class="nutrition-validation-slot">
                <div class="text-danger small"
                  *ngIf="productForm.get('nutrition.' + field.key)?.errors?.['nonNegative'] && productForm.get('nutrition.' + field.key)?.touched">
                  {{ productForm.get('nutrition.' + field.key)?.errors?.['nonNegative']?.message }}
                </div>
              </div>
            </div>
          </div>
          </ng-container>
        </div>
      </div>

      <!-- VENDOR & ORDER DETAILS -->
      <div class="row">
        <div class="col-12">
          <h6 class="section-title">Vendor &amp; Order Details</h6>
          <div class="mb-3">
            <label class="form-label">Search vendor</label>
            <ng-select
              [items]="vendorSearchResults"
              bindLabel="vendorName"
              placeholder="Type vendor ID or name…"
              [typeahead]="vendorSearchInput$"
              [loading]="vendorSearchLoading"
              [(ngModel)]="selectedVendorSearch"
              [ngModelOptions]="{standalone: true}"
              (change)="onVendorSearchSelected($event)"
              [clearable]="true"
              [minTermLength]="1">
              <ng-template ng-option-tmp let-item="item">
                <div>{{ item.vendorName || '—' }}</div>
                <small class="text-muted" *ngIf="item.vendorID">ID: {{ item.vendorID }}</small>
              </ng-template>
            </ng-select>
          </div>
          <div class="row mb-3 vendor-order-grid">
            <div class="col-md-4 mb-2">
              <label class="form-label">Vendor ID</label>
              <input type="text" class="form-control" formControlName="vendorID" placeholder="Supplier vendor ID">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">Vendor Name</label>
              <input type="text" class="form-control" formControlName="vendorName" placeholder="Supplier name">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">Order Number (vendor's article number)</label>
              <input type="text" class="form-control" formControlName="orderNumber" placeholder="Vendor article number">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">Producer / Manufacturer</label>
              <input type="text" class="form-control" formControlName="producer" placeholder="Producer or manufacturer">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">Country of Origin</label>
              <input type="text" class="form-control" formControlName="country" placeholder="e.g. Denmark">
            </div>
            <div class="col-md-2 mb-2">
              <label class="form-label">Coli (units per case)</label>
              <input type="number" class="form-control" formControlName="coli" min="1" step="1" inputmode="numeric" placeholder="1"
                [class.is-invalid]="productForm.get('coli')?.invalid && productForm.get('coli')?.touched">
              <div class="vendor-order-validation-slot">
                <div class="text-danger small"
                  *ngIf="productForm.get('coli')?.errors?.['integerMin'] && productForm.get('coli')?.touched">
                  {{ productForm.get('coli')?.errors?.['integerMin']?.message }}
                </div>
              </div>
            </div>
            <div class="col-md-2 mb-2">
              <label class="form-label">Shelf Life (days)</label>
              <input type="number" class="form-control" formControlName="shelfLifeDays" min="0" step="1" inputmode="numeric" placeholder="0"
                [class.is-invalid]="productForm.get('shelfLifeDays')?.invalid && productForm.get('shelfLifeDays')?.touched">
              <div class="vendor-order-validation-slot">
                <div class="text-danger small"
                  *ngIf="productForm.get('shelfLifeDays')?.errors?.['integerMin'] && productForm.get('shelfLifeDays')?.touched">
                  {{ productForm.get('shelfLifeDays')?.errors?.['integerMin']?.message }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- STORE PLACEMENT -->
      <div class="row">
        <div class="col-12">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="section-title mb-0">Store Placement</h6>
            <button *ngIf="product?._id" type="button" class="btn btn-sm btn-outline-secondary"
              (click)="openPlacementHistoryDialog()">
              <nb-icon icon="clock-outline" class="me-1"></nb-icon>
              View Placement History
            </button>
          </div>
          <p class="text-muted small mb-3">Physical shelf location for this store product. Changes are tracked as history and saved separately from other product fields.</p>
          <div class="row mb-3 store-placement-grid">
            <div class="col-md-4 mb-2">
              <label class="form-label">Area</label>
              <input type="text" class="form-control" formControlName="placementArea" placeholder="e.g. Beverages">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">Aisle</label>
              <input type="text" class="form-control" formControlName="placementAisle" placeholder="e.g. A3">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">Section</label>
              <input type="text" class="form-control" formControlName="placementSection" placeholder="e.g. Cold drinks">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">Shelf</label>
              <input type="text" class="form-control" formControlName="placementShelf" placeholder="e.g. S2">
            </div>
            <div class="col-md-4 mb-2">
              <label class="form-label">Position</label>
              <input type="text" class="form-control" formControlName="placementPosition" placeholder="e.g. Left">
            </div>
            <div class="col-md-2 mb-2">
              <label class="form-label">Shelf Capacity (units)</label>
              <input type="number" class="form-control" formControlName="shelfCapacityUnits" min="0" step="1" inputmode="numeric" placeholder="0"
                [class.is-invalid]="productForm.get('shelfCapacityUnits')?.invalid && productForm.get('shelfCapacityUnits')?.touched">
              <div class="store-placement-validation-slot">
                <div class="text-danger small"
                  *ngIf="productForm.get('shelfCapacityUnits')?.errors?.['nonNegative'] && productForm.get('shelfCapacityUnits')?.touched">
                  {{ productForm.get('shelfCapacityUnits')?.errors?.['nonNegative']?.message }}
                </div>
              </div>
            </div>
            <div class="col-md-2 mb-2">
              <label class="form-label">Shelf Facings</label>
              <input type="number" class="form-control" formControlName="shelfFacings" min="0" step="1" inputmode="numeric" placeholder="0"
                [class.is-invalid]="productForm.get('shelfFacings')?.invalid && productForm.get('shelfFacings')?.touched">
              <div class="store-placement-validation-slot">
                <div class="text-danger small"
                  *ngIf="productForm.get('shelfFacings')?.errors?.['integerMin'] && productForm.get('shelfFacings')?.touched">
                  {{ productForm.get('shelfFacings')?.errors?.['integerMin']?.message }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Inventory -->
        <div class="col-md-6">
          <h6 class="section-title">Inventory Management</h6>

          <div class="mb-3">
            <label class="form-label">Quantity *</label>
            <input type="number" class="form-control" formControlName="quantity" min="0">
            <div *ngIf="productForm.get('quantity')?.invalid && productForm.get('quantity')?.touched" class="text-danger small mt-1">
              Quantity is required
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Low Stock Threshold</label>
            <input type="number" class="form-control" formControlName="lowStockThreshold" min="0">
            <small class="form-text text-muted">Alert when stock falls below this number</small>
          </div>

          <div class="mb-3">
            <div class="form-check">
              <input type="checkbox" class="form-check-input" formControlName="trackInventory" id="trackInventory">
              <label class="form-check-label" for="trackInventory">Track Inventory</label>
            </div>
          </div>
        </div>

        <!-- Settings -->
        <div class="col-md-6">
          <h6 class="section-title">Product Settings</h6>

          <div class="mb-3">
            <div class="form-check">
              <input type="checkbox" class="form-check-input" formControlName="isVisible" id="isVisible">
              <label class="form-check-label" for="isVisible">Visible to Customers</label>
            </div>
          </div>

          <div class="mb-3">
            <div class="form-check">
              <input type="checkbox" class="form-check-input" formControlName="isFeatured" id="isFeatured">
              <label class="form-check-label" for="isFeatured">Featured Product</label>
            </div>
          </div>

          <!-- Age Restrictions -->
          <div class="mb-3">
            <div class="form-check">
              <input type="checkbox" class="form-check-input" formControlName="ageRestricted" id="ageRestricted">
              <label class="form-check-label" for="ageRestricted">
                <span class="me-1">🔞</span>Age Restricted Product
              </label>
            </div>
            <small class="form-text text-muted">Check if this product requires age verification</small>
          </div>

          <div *ngIf="productForm.get('ageRestricted')?.value" class="mb-3">
            <label class="form-label">Minimum Age Required *</label>
            <select class="form-select" formControlName="minimumAge">
              <option value="0">Select minimum age</option>
              <option value="16">16+ (Energy drinks, supplements)</option>
              <option value="18">18+ (Alcohol, tobacco)</option>
              <option value="21">21+ (Special restrictions)</option>
            </select>
            <small class="form-text text-muted">Customer age will be verified at checkout</small>
            <div *ngIf="productForm.get('ageRestricted')?.value && productForm.get('minimumAge')?.value <= 0" class="text-danger small mt-1">
              Please select a minimum age for age-restricted products
            </div>
          </div>

          <div *ngIf="productForm.get('ageRestricted')?.value" class="alert alert-warning mt-2">
            <nb-icon icon="alert-triangle-outline" class="me-2"></nb-icon>
            <strong>Age Verification Required:</strong> Customers must verify their age during checkout for this product.
          </div>

          <!-- Sold by Weight -->
          <div class="mt-4">
            <h6 class="section-title">Weight Selling</h6>
            <div class="mb-3">
              <div class="form-check">
                <input type="checkbox" class="form-check-input" formControlName="soldByWeight" id="soldByWeight">
                <label class="form-check-label" for="soldByWeight">
                  <span class="me-1">⚖️</span>Sold by Weight
                </label>
              </div>
              <small class="form-text text-muted">Check if this product is weighed at point of sale and priced per kg</small>
            </div>

            <div *ngIf="productForm.get('soldByWeight')?.value" class="sold-by-weight-fields">
              <div class="mb-3">
                <label class="form-label">Price per kg ({{ storeCurrency || 'DKK' }}) *</label>
                <div class="input-group">
                  <input type="number" class="form-control" formControlName="pricePerKg" step="0.01" min="0" placeholder="e.g. 90.00">
                  <span class="input-group-text">{{ storeCurrency || 'DKK' }}/kg</span>
                </div>
                <small class="form-text text-muted">Price per kilogram; used by the POS to calculate total from scale weight</small>
              </div>

              <!-- Weight Encoding dropdown hidden: encoding is now auto-derived from PLU prefix (20=price, 21=weight) -->
              <div *ngIf="false" class="mb-3">
                <label class="form-label">Weight Encoding *</label>
                <select class="form-select" formControlName="weightEncoding">
                  <option value="price">Price (øre) — scale encodes final price</option>
                  <option value="weight">Weight (grams) — scale encodes weight</option>
                </select>
                <small class="form-text text-muted">How the scale-generated EAN-13 encodes the variable part of the barcode</small>
              </div>

              <div class="alert alert-info mt-2">
                <nb-icon icon="scale-outline" class="me-2"></nb-icon>
                <strong>Scale Barcode:</strong> The POS decodes the EAN-13 barcode from the scale using the PLU prefix (20 = price-encoded, 21 = weight-encoded) to determine the final price automatically.
              </div>
            </div>
          </div>

          <!-- Deposit System -->
          <div *ngIf="depositSettings.enabled" class="mt-4">
            <div class="mb-3">
              <div class="form-check">
                <input type="checkbox" class="form-check-input" formControlName="hasDeposit" id="hasDeposit">
                <label class="form-check-label" for="hasDeposit">
                  <span class="me-1">🍾</span>Product Requires Deposit Fee
                </label>
              </div>
              <small class="form-text text-muted">Check if this product requires a recyclable deposit fee (bottles, cans, etc.)</small>
            </div>

            <!-- Deposit quantity (units per package) -->
            <div *ngIf="productForm.get('hasDeposit')?.value" class="mb-3">
              <label class="form-label">Units per package *</label>
              <input type="number" class="form-control" formControlName="depositQuantity" min="1" placeholder="e.g. 6">
              <small class="form-text text-muted">
                Enheder per pakke — number of individual deposit units inside one product (e.g. 6 for a 6-pack of cans)
              </small>
              <div *ngIf="productForm.get('depositQuantity')?.invalid && productForm.get('depositQuantity')?.touched" class="text-danger small mt-1">
                Units per package must be at least 1
              </div>
            </div>

            <!-- Dynamic deposit type dropdown (catalog-based) -->
            <div *ngIf="productForm.get('hasDeposit')?.value && depositFeeTypes.length > 0" class="mb-3">
              <label class="form-label">Deposit Type *</label>
              <select class="form-select" formControlName="depositFeeTypeId">
                <option value="">Select deposit type...</option>
                <option *ngFor="let ft of depositFeeTypes" [value]="ft._id">
                  {{ ft.name }}{{ ft.description ? ' — ' + ft.description : '' }} ({{ formatCurrency(ft.defaultAmount) }})
                </option>
              </select>
              <small class="form-text text-muted">Choose the deposit type based on container type</small>
              <div *ngIf="productForm.get('hasDeposit')?.value && productForm.get('depositFeeTypeId')?.invalid && productForm.get('depositFeeTypeId')?.touched" class="text-danger small mt-1">
                Please select a deposit type
              </div>
            </div>

            <!-- Provider deposit code — shown alongside fee type selection -->
            <div *ngIf="productForm.get('hasDeposit')?.value" class="mb-3">
              <label class="form-label">Provider deposit code <span class="text-muted small">(Optional)</span></label>
              <input type="text" class="form-control" formControlName="providerDepositCode" placeholder="e.g. 7000, 7024 — used for auto-matching on CSV import">
            </div>

            <!-- Legacy A/B/C dropdown — shown only when global catalog is empty -->
            <div *ngIf="productForm.get('hasDeposit')?.value && depositFeeTypes.length === 0" class="mb-3">
              <label class="form-label">Deposit Category *</label>
              <select class="form-select" formControlName="depositCategory">
                <option value="">Select deposit category</option>
                <option value="A">Category A - Glass bottles & metal cans (&lt;1L) - {{ formatCurrency(depositSettings.categoryA) }}</option>
                <option value="B">Category B - Plastic bottles (&lt;1L) - {{ formatCurrency(depositSettings.categoryB) }}</option>
                <option value="C">Category C - Large containers (1-20L) - {{ formatCurrency(depositSettings.categoryC) }}</option>
              </select>
              <small class="form-text text-muted">Choose the appropriate deposit category based on container type and size</small>
              <div *ngIf="productForm.get('hasDeposit')?.value && !productForm.get('depositCategory')?.value" class="text-danger small mt-1">
                Please select a deposit category for products with deposits
              </div>
            </div>

            <div *ngIf="productForm.get('hasDeposit')?.value && productForm.get('depositFeeTypeId')?.value" class="alert alert-info mt-2">
              <nb-icon icon="info-outline" class="me-2"></nb-icon>
              <strong>Deposit Amount:</strong> {{ formatCurrency(getDepositAmountByFeeTypeId(productForm.get('depositFeeTypeId')?.value)) }} will be added to the product price at checkout and refunded when the container is returned.
            </div>
            <div *ngIf="productForm.get('hasDeposit')?.value && !productForm.get('depositFeeTypeId')?.value && productForm.get('depositCategory')?.value" class="alert alert-info mt-2">
              <nb-icon icon="info-outline" class="me-2"></nb-icon>
              <strong>Deposit Amount:</strong> {{ formatCurrency(getDepositAmount(productForm.get('depositCategory')?.value)) }} will be added to the product price at checkout and refunded when the container is returned.
            </div>
          </div>

          <div *ngIf="!depositSettings.enabled" class="alert alert-secondary mt-2">
            <nb-icon icon="alert-circle-outline" class="me-2"></nb-icon>
            <strong>Deposit System Disabled:</strong> Contact your administrator to enable the deposit system for recyclable products.
          </div>

          <!-- Loyalty Discount Exclusion -->
          <div class="mt-4">
            <h6 class="section-title">Loyalty Discount</h6>
            <div class="mb-3">
              <div class="form-check">
                <input type="checkbox" class="form-check-input" formControlName="excludeFromLoyaltyDiscount" id="excludeFromLoyalty">
                <label class="form-check-label" for="excludeFromLoyalty">
                  <strong>Exclude from Loyalty Discount</strong>
                  <br>
                  <small class="text-muted">This product will not receive member loyalty discounts at checkout</small>
                </label>
              </div>
            </div>
          </div>

          <!-- Consumption Limits -->
          <div class="mt-4">
            <h6 class="section-title">Purchase Restrictions</h6>
            <div class="mb-3">
              <div class="form-check">
                <input type="checkbox" class="form-check-input" formControlName="hasConsumptionLimit" id="hasConsumptionLimit">
                <label class="form-check-label" for="hasConsumptionLimit">
                  <span class="me-1">🛒</span>Enable Purchase Limit
                </label>
              </div>
              <small class="form-text text-muted">Restrict how many times a customer can purchase this product within a specific time period</small>
            </div>

            <div *ngIf="productForm.get('hasConsumptionLimit')?.value" class="consumption-limit-fields">
              <div class="mb-3">
                <label class="form-label">Time Period *</label>
                <select class="form-select" formControlName="limitType">
                  <option value="">Select time period...</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
                <small class="form-text text-muted">The time period for the purchase limit</small>
                <div *ngIf="productForm.get('hasConsumptionLimit')?.value && productForm.get('limitType')?.invalid && productForm.get('limitType')?.touched" class="text-danger small mt-1">
                  Please select a time period
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Maximum Quantity *</label>
                <input type="number" class="form-control" formControlName="maxQuantity" min="1" placeholder="e.g., 2">
                <small class="form-text text-muted">Maximum quantity a customer can purchase per time period</small>
                <div *ngIf="productForm.get('hasConsumptionLimit')?.value && productForm.get('maxQuantity')?.invalid && productForm.get('maxQuantity')?.touched" class="text-danger small mt-1">
                  Maximum quantity must be at least 1
                </div>
              </div>

              <div class="alert alert-info mt-2">
                <nb-icon icon="info-outline" class="me-2"></nb-icon>
                <strong>Preview:</strong> {{ getConsumptionLimitPreview() }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Images -->
      <div class="row">
        <div class="col-12">
          <h6 class="section-title">Product Images</h6>

          <div class="mb-3">
            <label class="form-label">Upload Images <span class="text-muted fw-normal">(Optional)</span></label>
            <input type="file" class="form-control" (change)="onProductImagesSelect($event)" multiple accept="image/*">
            <small class="form-text text-muted">Optional. You can upload multiple images. Click on an image to set it as primary.</small>
          </div>

          <div *ngIf="showGlobalImagePreview" class="global-images-preview mb-3">
            <p class="text-muted small mb-2">Images will be copied from the global product when you save and link.</p>
            <div class="row g-2">
              <div class="col-auto" *ngFor="let url of getPendingGlobalImageUrls()">
                <img [src]="url" alt="Global product image" class="img-thumbnail global-pending-thumb">
              </div>
            </div>
          </div>

          <!-- Image Previews -->
          <div *ngIf="productImagePreviews.length > 0" class="image-previews">
            <div class="row">
              <div *ngFor="let preview of productImagePreviews; let i = index" class="col-md-3 mb-3">
                <div class="image-preview-container" [class.primary]="i === primaryImageIndex">
                  <img [src]="preview" [alt]="'Preview ' + (i + 1)" class="img-thumbnail w-100">
                  <div class="image-overlay">
                    <button type="button" class="btn btn-sm btn-primary" (click)="setPrimaryImage(i)" *ngIf="i !== primaryImageIndex">
                      Set Primary
                    </button>
                    <span *ngIf="i === primaryImageIndex" class="badge bg-warning">Primary</span>
                    <button type="button" class="btn btn-sm btn-danger" (click)="removeProductImage(i)">
                      <nb-icon icon="trash-outline"></nb-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions d-flex justify-content-end gap-2 mt-4">
        <button type="button" class="btn btn-outline-secondary" (click)="close()">Cancel</button>
        <button type="submit" class="btn btn-primary">
          {{ product ? 'Update Product' : 'Create Product' }}
        </button>
      </div>
    </form>
  </nb-card-body>
</nb-card>
